
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	GetStreamingChartInfoDocument,
	GetStreamingChartInfoQueryVariables,
} from '@/components/graphql/queries/GetStreamingChartInfo.query';

import { captureMessageForSentry } from '@/helpers/sentry-helper';

import {
	Platform,
	PopularityRankTypes,
	StreamingChartObjectType,
	StreamingChartsConnection,
	StreamingChartsFilter,
	StreamingChartsTitlesEdge,
} from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';

const Language = namespace('language');

/**
 * This function returns a list of titles to be displayed in a streaming chart.
 * @param titleId - The ID of the title which the streaming chart is centred around.
 * @param titleEdges - The array of title edges that can be displayed in the streaming chart.
 * @param range - The number of titles to be displayed before and after the central title in the streaming chart.
 * @returns A list of streaming chart titles with the central title placed in the middle position of the array.
 */
function sliceTitles(titleId: string, titleEdges: StreamingChartsTitlesEdge[], range: number) {
	const titles = titleEdges || [];
	const title = titles.find(title => title?.node?.id === titleId);
	const rank = title?.streamingChartInfo?.rank;

	// Set the start and end indices for default slicing.
	const sliceStart = range * 2 + 1 - range;
	const sliceEnd = range * 2 + 1 + range + 1;

	// If the title doesn't have a rank (e.g. is null/undefined), slice based on the fixed default range as a fallback.
	if (!rank) {
		return titles.slice(sliceStart, sliceEnd);
	}

	// Calculate the dynamic start and end indices for slicing. This is necessary when viewing titles in the top positions of the chart.
	// Dynamically calculate the beginning of the array to be sliced. If this number ends up being less than 0 (i.e. the title's rank is less than the specified range + 1), use 0.
	const dynamicSliceStart = Math.max(rank - (range + 1), 0);
	// Dynamically calculate the end of the array to be sliced. As we hit the top positions where dynamicSliceStart is 0, add the specified range (+1) on top to buff out the end of the list.
	const dynamicSliceEnd = rank + range + (dynamicSliceStart === 0 ? range + 1 - rank : 0);

	// If the rank is less than or equal to the total chart range, slice dynamically.
	if (rank <= range * 2 + 1) {
		return titles.slice(dynamicSliceStart, dynamicSliceEnd);
	}

	// Otherwise, slice based on the default fixed range.
	return titles.slice(sliceStart, sliceEnd);
}

@Component({
	name: 'ProvideStreamingChartData',
	apollo: {
		streamingCharts: {
			query: GetStreamingChartInfoDocument,
			variables(): GetStreamingChartInfoQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.streamingCharts;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Streaming Chart Data]:',
					{ error: error, where: 'Component: ProvideStreamingChartData' },
					'error'
				);
			},
		},
	},
})
export default class ProvideStreamingChartData extends Vue {
	streamingCharts: StreamingChartsConnection | undefined;

	@Prop() objectType: StreamingChartObjectType | undefined;
	@Prop() titleId: string | undefined;
	@Prop() titleName: string | undefined;
	@Prop() filterCountry: WebLocale | null | undefined;
	@Prop({ default: 4 }) range: number;
	@Prop({ default: () => ({}) }) filters: StreamingChartsFilter;
	@Prop({ default: false }) withOffers: boolean;

	@Language.Getter language: string;
	@Language.Getter country: string;

	get titles() {
		return this.basedOnTitle
			? sliceTitles(this.titleId!, this.streamingCharts?.edges || [], this.range)
			: this.streamingCharts?.edges || [];
	}

	get totalTitles() {
		return this.streamingCharts?.totalCount;
	}

	get queryVariables(): GetStreamingChartInfoQueryVariables {
		const filter = this.basedOnTitle
			? {
					jwEntityID: this.titleId,
					category: PopularityRankTypes.DailyPopularitySameContentType,
					previousTitles: this.range * 2 + 1,
					nextTitles: this.range * 2 + 1,
					objectType: this.objectType,
			  }
			: this.filters;
		const first = this.basedOnTitle ? (this.range * 2 + 1) * 2 + 1 : this.range;
		return {
			language: this.language,
			country: this.filterCountry !== undefined ? this.filterCountry : this.country,
			countryOffers: this.country,
			withOffers: this.withOffers,
			platform: Platform.Web,
			filter,
			first,
			after: '',
		};
	}

	get basedOnTitle() {
		return this.objectType && this.titleId && this.titleName;
	}

	get lastUpdated() {
		const title = this.titles.find(title => title?.node?.id === this.titleId) || ({} as StreamingChartsTitlesEdge);
		return title?.streamingChartInfo?.updatedAt;
	}

	get isDataLoading() {
		return this.$apollo.queries.streamingCharts?.loading;
	}

	loadMore() {
		// Bot protection
		if (navigator.webdriver) return;

		if (!this.streamingCharts?.pageInfo.hasNextPage) return;

		this.$apollo.queries.streamingCharts?.fetchMore({
			variables: {
				after: this.streamingCharts?.pageInfo.endCursor,
			},
			updateQuery(previousResult, { fetchMoreResult }) {
				return {
					streamingCharts: {
						__typename: previousResult.streamingCharts.__typename,
						edges: [...previousResult.streamingCharts.edges, ...fetchMoreResult.streamingCharts.edges],
						pageInfo: fetchMoreResult.streamingCharts.pageInfo,
						totalCount: fetchMoreResult.streamingCharts.totalCount,
					},
				};
			},
		});
	}

	render() {
		if (this.$scopedSlots.default) {
			const titles = this.titles || [];
			return this.$scopedSlots.default({
				titles,
				totalTitles: this.totalTitles || 0,
				firstTitle: titles[0],
				titleId: this.titleId,
				titleName: this.titleName,
				lastUpdated: this.lastUpdated,
				isLoading: this.isDataLoading,
				loadMore: this.loadMore,
			});
		}

		return this.$slots[0];
	}
}
